import moment from 'moment';
import React, { Dispatch, useCallback, useMemo } from 'react';

import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { RectangleButton, Text } from '@shared/ui-components';
import { RenderIf } from 'common/components/render-if';
import { PaymentConstants } from 'common/constants/payment';
import { State } from 'common/interfaces/state';
import { KreoDialogActions } from 'common/UIKit';
import { AccountSelectors } from 'unit-account/selectors';
import { CompanySubscriptionModel } from '../../../../units/account/interfaces/company-subscription-model';
import { SubscriptionActivityStatus } from '../../../../units/subscription/enums/subscription-activity-status';
import { EstimateSubscriptionModel } from '../../../../units/subscription/interfaces/estimation-subscription-model';
import { PAYMENTS_DETAILS_DIALOG, PaymentDetailsDialog } from './payment-details-dialog';
import { Styled } from './styled';

const SUBSCRIPTION_DATE_FORMAT = 'D MMM YYYY';


interface StateProps {
  subscription: CompanySubscriptionModel;
}

interface DispatchProps {
  openDialog: (name: string) => void;
}


interface Props extends DispatchProps, StateProps {
  billingEstimation: EstimateSubscriptionModel;
}

const PaymentDetailsBlockComponent: React.FC<Props> = ({
  subscription, billingEstimation, openDialog,
}: Props) => {
  const isTrialActive = subscription
    && subscription.trialEnd
    && subscription.billingStatus === SubscriptionActivityStatus.Trial;

  const total = useMemo(() => {
    const invoice = billingEstimation && (billingEstimation.nextInvoice || billingEstimation.immediateInvoice);
    if (!invoice) {
      return null;
    }

    return invoice.total / 100;
  }, [billingEstimation]);

  const nextBillingDate = useMemo(() => {
    return billingEstimation && billingEstimation.nextInvoice && billingEstimation.nextBillingAt
      ? moment(billingEstimation.nextBillingAt).format(SUBSCRIPTION_DATE_FORMAT)
      : subscription && subscription.nextBillingAt
        ? moment(subscription.nextBillingAt).format(SUBSCRIPTION_DATE_FORMAT)
        : null;
  }, [billingEstimation, subscription]);

  const handleOnDetailsClick = useCallback(() => {
    openDialog(PAYMENTS_DETAILS_DIALOG);
  }, [openDialog]);

  if (!nextBillingDate && !total && !isTrialActive) {
    return null;
  }

  return (
    <Styled.PaymentDetails>
      <RenderIf condition={isTrialActive}>
        <Text fontSize={14}>
          <Text fontSize={14} color="additionalFont">
            <pre>{'Trial end at '}</pre>
          </Text>
          <pre>
            {moment(subscription && subscription.trialEnd).format(SUBSCRIPTION_DATE_FORMAT)}
          </pre>
        </Text>
      </RenderIf>
      <RenderIf condition={!!nextBillingDate}>
        <Text fontSize={14}>
          <Text fontSize={14} color="additionalFont">
            <pre>{'Next billing at '}</pre>
          </Text>
          <pre>{nextBillingDate}</pre>
        </Text>
      </RenderIf>
      <RenderIf condition={!!total}>
        <Text fontSize={14}>
          <Text fontSize={14} color="additionalFont">
            <pre>{'Total: '}</pre>
          </Text>
          {PaymentConstants.defaultCurrency}{total}
        </Text>
        <RenderIf condition={!!billingEstimation}>
          <Styled.DetailsButton>
            <RectangleButton
              size="s"
              height={18}
              width={72}
              onClick={handleOnDetailsClick}
              text={'More details'}
            />
          </Styled.DetailsButton>
        </RenderIf>
      </RenderIf>
      <PaymentDetailsDialog />
    </Styled.PaymentDetails>
  );
};

function mapStateToProps(state: State): StateProps {
  return {
    subscription: AccountSelectors.currentSubscription(state),
  };
}

function mapDispatchToProps(dispatch: Dispatch<AnyAction>): DispatchProps {
  return {
    openDialog: (name) => dispatch(KreoDialogActions.openDialog(name)),
  };
}

export const PaymentDetailsBlock = connect(mapStateToProps, mapDispatchToProps)(PaymentDetailsBlockComponent);
